/**
 * Live WhatsApp Integration Helper
 * Provides 1-click WhatsApp Web / App deep-linking (https://wa.me/)
 * and Meta WhatsApp Cloud API integration.
 */

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

/** Strip to a validated Indian 10-digit mobile (6–9…), or null. */
export const cleanIndianPhone = (phoneStr: string): string | null => {
  if (!phoneStr) return null;
  let digits = String(phoneStr).replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith("0"))
    digits = digits.slice(1);
  else if (digits.length > 10) digits = digits.slice(-10);
  return /^[6789]\d{9}$/.test(digits) ? digits : null;
};

// Helper to format Indian 10-digit phone numbers for WhatsApp API / wa.me
export const formatWhatsAppPhone = (phoneStr: string): string => {
  if (!phoneStr) return "";
  const cleaned = cleanIndianPhone(phoneStr);
  if (cleaned) return `91${cleaned}`;
  const digits = phoneStr.replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  if (digits.length > 10) return digits.slice(-12);
  return digits;
};

/**
 * Direct 1-Click WhatsApp Web / Desktop / Mobile App Launcher
 * Opens official https://wa.me/ URL with pre-filled message text.
 */
export const openWhatsAppWeb = (phone: string, message: string) => {
  const formattedPhone = formatWhatsAppPhone(phone);
  const encodedText = encodeURIComponent(message);
  const url = `https://wa.me/${formattedPhone}?text=${encodedText}`;
  window.open(url, "_blank");
};

const messagingEndpoint = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return supabaseUrl
    ? `${supabaseUrl}/functions/v1/send-whatsapp-message`
    : "/api/send-whatsapp-message";
};

const messagingHeaders = async (): Promise<HeadersInit> => {
  const publishableKey =
    import.meta.env.VITE_SB_PUBLISHABLE_KEY ||
    import.meta.env.VITE_SUPABASE_ANON_KEY ||
    "";

  let accessToken = "";
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    accessToken = data.session?.access_token || "";
  } catch {
    // FakeRest / no auth session
  }

  const bearer = accessToken || publishableKey;
  return {
    "Content-Type": "application/json",
    ...(bearer
      ? {
          Authorization: `Bearer ${bearer}`,
          ...(publishableKey ? { apikey: publishableKey } : {}),
        }
      : {}),
  };
};

/** Meta HSM used to re-open a chat outside the 24h window (test default: hello_world). */
export const DEFAULT_REENGAGE_TEMPLATE =
  (import.meta.env.VITE_WHATSAPP_REENGAGE_TEMPLATE as string | undefined)?.trim() ||
  "hello_world";

export const DEFAULT_REENGAGE_TEMPLATE_LANG =
  (
    import.meta.env.VITE_WHATSAPP_REENGAGE_TEMPLATE_LANG as string | undefined
  )?.trim() || "en_US";

export type MetaCloudWhatsAppExtras = {
  contactId?: string | number;
  skipLog?: boolean;
  messageSource?: string;
  mediaUrl?: string;
  templateName?: string;
  templateLanguage?: string;
};

function formatClientMetaError(data: {
  error?: string;
  meta_error_code?: number | null;
  metaResponse?: {
    error?: {
      code?: number;
      message?: string;
      title?: string;
      error_data?: { details?: string };
    };
  };
}): string {
  if (data.error?.trim()) return data.error.trim();

  const err = data.metaResponse?.error;
  const code = data.meta_error_code ?? err?.code;
  const details = err?.error_data?.details;
  const base = [code != null ? `(#${code})` : "", err?.title || err?.message]
    .filter(Boolean)
    .join(" ");

  if (code === 131047 || /re-engagement|24 hours/i.test(`${base} ${details}`)) {
    return `${base || "(#131047) Re-engagement message"}${details ? ` — ${details}` : ""}. Outside the 24h window: ask the contact to reply, or send an approved template (e.g. ${DEFAULT_REENGAGE_TEMPLATE}) first.`;
  }
  if (code === 131030) {
    return `${base || "(#131030) Recipient not in allowed list"}. Add this number in Meta → WhatsApp → API Setup → To (trusted contacts).`;
  }
  return (
    details ||
    base ||
    "WhatsApp send failed"
  );
}

/**
 * Serverless Meta Cloud API Client / Supabase Edge Function Call
 * Dispatches official WhatsApp HSM Template Messages (Brochure PDF, Site Visit Pass, Payment Demand)
 */
export const sendMetaCloudWhatsApp = async (
  phone: string,
  messageBody: string,
  mediaUrl?: string,
  extras?: MetaCloudWhatsAppExtras,
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const formattedPhone = formatWhatsAppPhone(phone);
    const resolvedMediaUrl = mediaUrl ?? extras?.mediaUrl;
    const templateName = extras?.templateName?.trim();
    // Template-only re-engage: omit free-form body so the edge function uses HSM.
    const message = templateName ? "" : messageBody;

    const response = await fetch(messagingEndpoint(), {
      method: "POST",
      headers: await messagingHeaders(),
      body: JSON.stringify({
        phone: formattedPhone,
        message,
        media_url: resolvedMediaUrl,
        contact_id: extras?.contactId,
        skip_log: extras?.skipLog,
        message_source: extras?.messageSource,
        template_name: templateName || undefined,
        template_language:
          extras?.templateLanguage ||
          (templateName ? DEFAULT_REENGAGE_TEMPLATE_LANG : undefined),
      }),
    });

    const data = (await response.json().catch(() => ({}))) as {
      status?: string;
      messageId?: string;
      error?: string;
      meta_error_code?: number | null;
      metaResponse?: {
        error?: {
          code?: number;
          message?: string;
          title?: string;
          error_data?: { details?: string };
        };
      };
    };

    if (
      response.ok &&
      (data.status === "sent" ||
        data.status === "campaign" ||
        data.status === "auto_first_touch")
    ) {
      return {
        success: true,
        messageId: data.messageId || "msg_" + Date.now(),
      };
    }

    return {
      success: false,
      error:
        formatClientMetaError(data) ||
        `WhatsApp send failed (${response.status}${data.status ? `: ${data.status}` : ""})`,
    };
  } catch (err: unknown) {
    console.warn("Meta Cloud API offline:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Meta Cloud API offline",
    };
  }
};

/** Outbound email via the same messaging edge function (Postmark). */
export const sendDemandEmail = async (opts: {
  to: string;
  subject: string;
  textBody: string;
  htmlBody?: string;
  pdfBase64?: string;
  pdfFilename?: string;
}): Promise<{ success: boolean; status?: string; error?: string }> => {
  try {
    const response = await fetch(messagingEndpoint(), {
      method: "POST",
      headers: await messagingHeaders(),
      body: JSON.stringify({
        channel: "email",
        to_email: opts.to,
        subject: opts.subject,
        message: opts.textBody,
        html_body: opts.htmlBody,
        pdf_base64: opts.pdfBase64,
        pdf_filename: opts.pdfFilename,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        error: data.error || `Email failed (${response.status})`,
      };
    }
    return { success: true, status: data.status };
  } catch (err: unknown) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Email dispatch failed",
    };
  }
};
